/**
 * Importimi i regjistrit të aseteve nga një fletë e jashtme (Excel/CSV).
 *
 * Asetet e një kompanie nuk nisin te Nexuma: ato jetojnë prej vitesh në një
 * fletë Excel të dikujt, me kolona të shkruara për sy të tjerë — «Item»,
 * «Serial No», «Assigned to», «Bought», me data në tre formate dhe me statuse
 * si "In use" ose "Në depo". Ajo fletë është regjistri i vërtetë i klientit;
 * puna e këtij moduli është ta pranojë ashtu siç është, jo ta detyrojë njeriun
 * ta rishkruajë.
 *
 * Prandaj gjithçka që VENDOS rri këtu, në funksione të pastra pa React dhe pa
 * rrjet: cila kolonë është cila, çfarë do të thotë "In use", a është "05/03"
 * marsi apo maji, cili rresht përplaset me një aset që ekziston tashmë.
 * Dialogu (`components/assets/AssetsImportDialog.js`) vetëm i vizaton, serveri
 * (`backend/services/assetsImport.js`) vetëm i shkruan. Rregullat mbahen nga
 * `assetsImport.test.js`.
 *
 * Dy vendime e ndajnë këtë import nga një ngarkim i thjeshtë tabele:
 *
 * 1. **Mbajtësi importohet bashkë me pajisjen.** Një regjistër ku 80 laptopë
 *    hyjnë të gjithë si "Në dispozicion" është i rremë që në minutën e parë —
 *    ata janë në duar njerëzish. Kolona «Mbajtësi» hap një caktim të vërtetë,
 *    me datë dorëzimi, pra edhe historikun që e provon.
 * 2. **Asnjë rresht nuk shkon te serveri pa u parë nga njeriu.** Importi është
 *    veprim që s'kthehet me një klik, dhe një aset i dyfishuar e ndan
 *    historikun e vet në dysh pa u vënë re.
 */
import * as XLSX from 'xlsx-js-style';
import {
  normKey, parseDateCell, parseNumber, matchVocabulary, splitList, todayISO,
  detectMappingFor, unmappedHeaders, readWorkbookWith,
} from './importCells';
import {
  CATEGORIES, CONDITIONS, STATUSES, PAYMENT_METHODS, SPEC_FIELDS,
  categoryLabel, conditionLabel, statusLabel,
  identifierLabel, specKey,
} from '../config/assets';

export { unmappedHeaders };

const HEX = {
  ink: '111827', muted: '6B7280', rule: 'E2E8F0', brand: '4893A5', navy: '303D50',
  soft: 'F8FAFC', tint: 'EDF4F6', req: 'FEF3C7', list: 'F1F5F9', white: 'FFFFFF',
};

// ── Fjalorët: si e shkruan njeriu atë që sistemi e mban si çelës ─────────────
//
// Fjalori i aseteve (`config/assets.js`) mban çelësin dhe etiketën shqip. Fleta
// e klientit mban çkado: "In use", "Neu", "Repair", "Sold". Këto lista janë ura
// mes të dyjave — dhe ura shkon vetëm në një drejtim: vlera e panjohur NUK
// hamendësohet, ajo bëhet paralajmërim te rreshti.
const CATEGORY_WORDS = {
  laptop:    ['notebook', 'macbook', 'laptopi', 'portable', 'ultrabook'],
  desktop:   ['pc', 'desktop', 'kompjuteri', 'kompjuter tavoline', 'workstation', 'all in one', 'rechner'],
  phone:     ['telefoni', 'mobile', 'smartphone', 'celular', 'handy', 'iphone', 'mobil'],
  monitor:   ['ekran', 'ekrani', 'display', 'screen', 'bildschirm', 'monitori'],
  vehicle:   ['vetura', 'veture', 'makina', 'auto', 'car', 'vehicle', 'fahrzeug', 'kamion', 'kombi', 'motocikleta'],
  tool:      ['vegel', 'vegla', 'tool', 'tools', 'werkzeug', 'makineri', 'pajisje', 'pajisje pune', 'equipment'],
  furniture: ['mobilie', 'mobilia', 'furniture', 'karrige', 'tavoline', 'dollap', 'mobel', 'mobiliar'],
  access:    ['celes', 'celesa', 'qasje', 'key', 'keys', 'access', 'kartele hyrje', 'schlussel', 'licence', 'licenca', 'account'],
  other:     ['tjeter', 'te tjera', 'other', 'sonstiges', 'misc', 'various'],
};

const CONDITION_WORDS = {
  new:      ['i ri', 'e re', 're', 'new', 'neu', 'e paperdorur', 'pa perdorur', 'brand new', 'i paperdorur'],
  good:     ['mire', 'e mire', 'i mire', 'good', 'ok', 'ne rregull', 'funksionale', 'used', 'e perdorur', 'gut', 'fine'],
  damaged:  ['demtuar', 'e demtuar', 'damaged', 'defekt', 'defective', 'beschadigt', 'e prishur', 'me defekt'],
  unusable: ['jashte perdorimit', 'jashte', 'unusable', 'skrap', 'scrap', 'write off', 'nuk perdoret', 'e pariparueshme', 'defekt total'],
};

const STATUS_WORDS = {
  available: ['dispozicion', 'ne dispozicion', 'lire', 'e lire', 'depo', 'ne depo', 'stock', 'in stock', 'available', 'frei', 'lager', 'pa perdorues'],
  assigned:  ['caktuar', 'e caktuar', 'te punonjesi', 'ne perdorim', 'in use', 'assigned', 'issued', 'ausgegeben', 'e dorezuar', 'dorezuar'],
  service:   ['servis', 'ne servis', 'riparim', 'ne riparim', 'service', 'repair', 'wartung', 'maintenance', 'ne mirembajtje'],
  lost:      ['humbur', 'e humbur', 'lost', 'vjedhur', 'e vjedhur', 'stolen', 'verloren', 'zhdukur'],
  retired:   ['larguar', 'e larguar', 'retired', 'sold', 'shitur', 'e shitur', 'skarcuar', 'jashte inventarit', 'disposed', 'ausgemustert', 'shkatrruar'],
};

const PAYMENT_WORDS = {
  bank:    ['transfer', 'transfer bankar', 'bank', 'banke', 'wire', 'uberweisung', 'llogari bankare', 'bank transfer'],
  cash:    ['para ne dore', 'cash', 'kesh', 'ne dore', 'bar', 'para'],
  card:    ['karte', 'kartele', 'card', 'credit card', 'debit card', 'kartele bankare', 'visa'],
  leasing: ['leasing', 'lizing', 'qira financiare', 'qira'],
  other:   ['tjeter', 'other', 'sonstiges'],
};

/** Fjalori i modulit + sinonimet e fletëve të huaja, si një hartë e vetme. */
const vocabulary = (list, extra) => Object.fromEntries(
  list.map((x) => [x.key, [x.label, ...(extra[x.key] || [])]]),
);

const CATEGORY_VOCAB  = vocabulary(CATEGORIES, CATEGORY_WORDS);
const CONDITION_VOCAB = vocabulary(CONDITIONS, CONDITION_WORDS);
const STATUS_VOCAB    = vocabulary(STATUSES, STATUS_WORDS);
const PAYMENT_VOCAB   = vocabulary(PAYMENT_METHODS, PAYMENT_WORDS);

/** Kategoria e shkruar → çelësi i fjalorit, ose `null` kur nuk njihet. */
export const parseCategory  = (v) => matchVocabulary(v, CATEGORY_VOCAB);
export const parseCondition = (v) => matchVocabulary(v, CONDITION_VOCAB);
export const parseStatus    = (v) => matchVocabulary(v, STATUS_VOCAB);
export const parsePayment   = (v) => matchVocabulary(v, PAYMENT_VOCAB);

const CURRENCY_SIGNS = { '€': 'EUR', $: 'USD', '£': 'GBP', '₣': 'CHF' };
const CURRENCY_WORDS = {
  EUR: ['euro', 'eur', 'evro'], USD: ['usd', 'dollar', 'dollare', 'dolar'],
  CHF: ['chf', 'frang', 'franga'], GBP: ['gbp', 'pound', 'funte'],
};

/**
 * Monedha → kod tre-shkronjësh, ose `null`.
 *
 * Kolona vjen si «€», «EUR», «Euro» ose bosh. Bosh NUK është gabim: kolona te
 * baza ka parazgjedhjen 'EUR', dhe një kompani që blen gjithçka në euro nuk ka
 * pse ta shkruajë njëqind herë.
 */
export function parseCurrency(v) {
  const raw = String(v ?? '').trim();
  if (!raw) return null;
  if (CURRENCY_SIGNS[raw]) return CURRENCY_SIGNS[raw];
  const k = normKey(raw);
  for (const [code, words] of Object.entries(CURRENCY_WORDS)) {
    if (words.includes(k)) return code;
  }
  return /^[a-z]{3}$/.test(k) ? k.toUpperCase() : null;
}

/**
 * «CPU: i7-1165G7; RAM: 16 GB» → `{ cpu: 'i7-1165G7', ram: '16 GB' }`.
 *
 * Etiketa e njohur merr çelësin kanonik (`specKey`), e shpikura mban vetveten —
 * pikërisht si te formulari i asetit, ndaj një specifikim i importuar lexohet
 * njësoj si një i shkruar me dorë. Copat pa dy pika kthehen te `stray`, që
 * ekrani të thotë çfarë nuk u kuptua në vend që ta heqë në heshtje.
 */
export function parseSpecsText(v) {
  const specs = {};
  const stray = [];
  for (const part of splitList(v)) {
    const at = part.indexOf(':');
    if (at < 1) { stray.push(part); continue; }
    const key = specKey(part.slice(0, at));
    const value = part.slice(at + 1).trim();
    if (key && value) specs[key] = value; else stray.push(part);
  }
  return { specs, stray };
}

/**
 * Emri i një fotoje → çelës përputhjeje.
 *
 * Bie shtegu (`C:\\fotot\\laptop.jpg` → `laptop.jpg`), bie prapashtesa dhe biem
 * te `normKey`: «Laptop Dell.JPG», «laptop-dell.jpg» dhe «laptop_dell» janë i
 * njëjti skedar për sytë e atij që e plotësoi fletën, ndaj duhet të jenë i
 * njëjti edhe këtu. Pa këtë tolerancë, gjysma e fotove do të mbetej pa u
 * ngarkuar për një vizë ndarëse.
 */
export function photoKey(name) {
  const base = String(name ?? '').trim().split(/[\\/]/).pop().split('?')[0];
  return normKey(base.replace(/\.[a-z0-9]{2,5}$/i, ''));
}

// ── Seksionet ────────────────────────────────────────────────────────────────
// Tridhjetë kolona në një rresht janë listë, jo formë. Seksionet e ndajnë si i
// ndan njeriu që e mban inventarin: çka është pajisja, ku është, sa kushtoi,
// kush e mban, çka ka brenda.
export const SECTIONS = [
  { key: 'identity', label: 'Identiteti i pajisjes' },
  { key: 'state',    label: 'Gjendja dhe vendndodhja' },
  { key: 'purchase', label: 'Blerja dhe financat' },
  { key: 'holder',   label: 'Mbajtësi aktual' },
  { key: 'extra',    label: 'Specifikime, aksesorë, shënime' },
  { key: 'specs',    label: 'Specifikime si kolona më vete' },
];

// ── Regjistri i kolonave ─────────────────────────────────────────────────────
// Një listë e vetme e ushqen shabllonin, njohjen automatike, hartën e
// redaktueshme, validimin dhe ngarkesën për API-në. Kolonë e re → një zë këtu,
// asgjë tjetër.
//
// Rendi është ai i rëndësisë: e detyrueshmja e para, pastaj ajo që e bën
// pajisjen të gjendet (seria, kodi), pastaj gjendja, financat, mbajtësi.
export const IMPORT_COLUMNS = [
  // ── Identiteti ────────────────────────────────────────────────────────────
  {
    key: 'name', label: 'Emri i asetit', section: 'identity', required: true, type: 'text', width: 26,
    aliases: ['emri', 'emri i asetit', 'emri i pajisjes', 'pajisja', 'artikulli', 'aseti', 'asset', 'asset name', 'item', 'item name', 'equipment', 'bezeichnung', 'gegenstand', 'produkti'],
  },
  {
    key: 'category', label: 'Kategoria', section: 'identity', type: 'category', width: 16,
    aliases: ['kategoria', 'category', 'lloji', 'lloji i pajisjes', 'type', 'asset type', 'kategorie', 'art', 'grupi'],
  },
  {
    key: 'subcategory', label: 'Nënkategoria', section: 'identity', type: 'text', width: 16,
    aliases: ['nenkategoria', 'subcategory', 'sub category', 'nengrupi', 'unterkategorie'],
  },
  {
    key: 'brand', label: 'Marka', section: 'identity', type: 'text', width: 16,
    aliases: ['marka', 'brand', 'make', 'manufacturer', 'prodhuesi', 'hersteller', 'marke'],
  },
  {
    key: 'model', label: 'Modeli', section: 'identity', type: 'text', width: 20,
    aliases: ['modeli', 'model', 'modell', 'tipi'],
  },
  {
    // Një kolonë, tri emra: te një telefon njeriu shkruan IMEI, te një automjet
    // VIN. Kërkimi i gjen të gjitha me një fushë pikërisht sepse janë një kolonë.
    key: 'serialNumber', label: 'Serial / IMEI / VIN', section: 'identity', type: 'text', width: 24, unique: true,
    aliases: ['serial', 'seria', 'numri serik', 'serial number', 'serial no', 'sn', 'imei', 'vin', 'shasia', 'chassis', 'seriennummer', 'nr serik'],
  },
  {
    key: 'internalCode', label: 'Kodi i brendshëm', section: 'identity', type: 'text', width: 18, unique: true,
    aliases: ['kodi i brendshem', 'kodi i inventarit', 'inventar', 'inventory number', 'internal code', 'asset tag', 'tag', 'inventarnummer', 'nr inventari'],
  },
  {
    // Bosh → sekuenca e jep vetë (AST-00042). Kodi i sjellë nga sistemi i vjetër
    // ruhet ashtu siç është: ai jeton te faturat dhe te etiketat e ngjitura.
    key: 'assetCode', label: 'Kodi i asetit (AST-…)', section: 'identity', type: 'text', width: 18, unique: true,
    aliases: ['kodi i asetit', 'kodi', 'asset code', 'asset id', 'code', 'anlagennummer'],
  },

  // ── Gjendja dhe vendndodhja ───────────────────────────────────────────────
  {
    key: 'condition', label: 'Gjendja fizike', section: 'state', type: 'condition', width: 16,
    aliases: ['gjendja', 'gjendja fizike', 'condition', 'zustand', 'state', 'gjendje'],
  },
  {
    key: 'status', label: 'Statusi', section: 'state', type: 'status', width: 16,
    aliases: ['statusi', 'status', 'situata', 'availability', 'verfugbarkeit'],
  },
  {
    key: 'location', label: 'Lokacioni', section: 'state', type: 'text', width: 22,
    aliases: ['lokacioni', 'vendndodhja', 'location', 'zyra', 'kati', 'office', 'room', 'standort', 'ort', 'depo'],
  },
  {
    key: 'department', label: 'Departamenti', section: 'state', type: 'department', width: 20,
    aliases: ['departamenti', 'department', 'dept', 'abteilung', 'sektori', 'njesia'],
  },
  {
    // Përgjegjësi NUK është mbajtësi: përgjegjësi është ai që i përgjigjet për
    // pajisjen (shefi i IT-së për njëzet laptopë), mbajtësi është ai që e ka në
    // duar. Ngatërrimi i tyre e bën çdo raport pajisjesh të pavlefshëm.
    key: 'responsible', label: 'Personi përgjegjës', section: 'state', type: 'worker', width: 22,
    aliases: ['personi pergjegjes', 'pergjegjesi', 'responsible', 'owner', 'custodian', 'verantwortlich', 'kujdestari'],
  },

  // ── Blerja dhe financat ───────────────────────────────────────────────────
  {
    key: 'purchaseDate', label: 'Data e blerjes', section: 'purchase', type: 'date', width: 15,
    aliases: ['data e blerjes', 'blerja', 'purchase date', 'bought', 'date of purchase', 'kaufdatum', 'data e fatures', 'data'],
  },
  {
    key: 'purchaseValue', label: 'Vlera e blerjes', section: 'purchase', type: 'money', width: 15,
    aliases: ['vlera e blerjes', 'vlera', 'cmimi', 'price', 'purchase value', 'cost', 'value', 'amount', 'kaufpreis', 'wert', 'shuma'],
  },
  {
    key: 'currency', label: 'Monedha', section: 'purchase', type: 'currency', width: 10,
    aliases: ['monedha', 'currency', 'valuta', 'wahrung'],
  },
  {
    key: 'supplier', label: 'Furnitori', section: 'purchase', type: 'text', width: 22,
    aliases: ['furnitori', 'shitesi', 'supplier', 'vendor', 'seller', 'lieferant', 'kompania shitese'],
  },
  {
    key: 'invoiceNo', label: 'Nr. i faturës', section: 'purchase', type: 'text', width: 16,
    aliases: ['nr i fatures', 'numri i fatures', 'fatura', 'invoice', 'invoice no', 'invoice number', 'rechnungsnummer', 'nr fature'],
  },
  {
    key: 'paymentMethod', label: 'Mënyra e pagesës', section: 'purchase', type: 'payment', width: 18,
    aliases: ['menyra e pageses', 'pagesa', 'payment', 'payment method', 'zahlungsart', 'si u pagua'],
  },
  {
    // Pa vitet e amortizimit "vlera e mbetur" nuk llogaritet dot — dhe pikërisht
    // ajo shifër i duhet kontabilitetit, jo çmimi i blerjes.
    key: 'amortizationYears', label: 'Amortizimi (vite)', section: 'purchase', type: 'number', width: 17,
    aliases: ['amortizimi', 'amortizimi vite', 'amortization', 'depreciation', 'depreciation years', 'jeta e dobishme', 'useful life', 'abschreibung'],
  },
  {
    key: 'warrantyUntil', label: 'Garancioni deri më', section: 'purchase', type: 'date', width: 18,
    aliases: ['garancioni', 'garancioni deri me', 'garancia', 'warranty', 'warranty until', 'warranty expiry', 'garantie', 'skadimi i garancionit'],
  },

  // ── Mbajtësi aktual ───────────────────────────────────────────────────────
  {
    // Kolona për të cilën ekziston i gjithë ky import. Pa të, regjistri i ri
    // thotë se çdo pajisje rri në depo — dhe kjo është e pavërtetë që në ditën
    // e parë, sepse laptopët janë në duar njerëzish.
    key: 'holder', label: 'Mbajtësi aktual (punonjësi)', section: 'holder', type: 'worker', width: 26,
    aliases: ['mbajtesi', 'mbajtesi aktual', 'punonjesi', 'perdoruesi', 'te kush', 'assigned to', 'holder', 'user', 'employee', 'mitarbeiter', 'benutzer', 'kush e mban'],
  },
  {
    key: 'assignedOn', label: 'Data e dorëzimit', section: 'holder', type: 'date', width: 17,
    aliases: ['data e dorezimit', 'dorezimi', 'assigned on', 'assigned date', 'date assigned', 'handover date', 'ausgabedatum', 'data e marrjes'],
  },

  // ── Specifikime, aksesorë, shënime ────────────────────────────────────────
  {
    key: 'specsText', label: 'Specifikimet', section: 'extra', type: 'specs', width: 40,
    aliases: ['specifikimet', 'specifikime', 'specs', 'specification', 'specifications', 'technische daten', 'detajet teknike', 'konfigurimi'],
  },
  {
    key: 'accessories', label: 'Aksesorët', section: 'extra', type: 'list', width: 28,
    aliases: ['aksesoret', 'aksesore', 'accessories', 'shtojcat', 'zubehor', 'pajisjet shtese', 'included'],
  },
  {
    // Fotoja nuk hyn dot brenda Excel-it — kjo kolonë mban EMRIN e skedarit, dhe
    // skedarët vetë zgjidhen te dialogu. Krejt opsionale: një regjistër pa foto
    // është ende regjistër, kurse një import që kërkon foto për çdo rresht nuk
    // do të mbaronte kurrë.
    key: 'photo', label: 'Fotoja (emri i skedarit)', section: 'extra', type: 'photo', width: 26,
    aliases: ['fotoja', 'foto', 'fotografia', 'imazhi', 'photo', 'image', 'picture', 'bild', 'skedari i fotos', 'file name'],
  },
  {
    key: 'description', label: 'Përshkrimi', section: 'extra', type: 'text', width: 30,
    aliases: ['pershkrimi', 'description', 'beschreibung', 'detajet'],
  },
  {
    key: 'notes', label: 'Shënime', section: 'extra', type: 'text', width: 28,
    aliases: ['shenime', 'shenim', 'notes', 'note', 'comment', 'koment', 'bemerkung', 'remarks', 'verejtje'],
  },
];

/**
 * Specifikimet si KOLONA më vete — të njohura, por jo te shablloni.
 *
 * Shablloni ynë e mban një kolonë të vetme «Specifikimet» («CPU: i7; RAM: 16»),
 * sepse njëzet kolona teknike do ta bënin fletën të palexueshme dhe shumica do
 * të rrinin bosh: një karrige nuk ka CPU. Por fleta e KLIENTIT i ka shpesh si
 * kolona — «CPU», «RAM», «Targat», «Kilometrazhi» — dhe ato nuk guxojnë të
 * humbin. Prandaj çdo fushë e njohur specifikimi ekziston si destinacion i
 * hartës; kur fleta s'e ka, kolona thjesht mbetet e palidhur.
 */
export const SPEC_COLUMNS = Array.from(
  new Map(
    Object.values(SPEC_FIELDS).flat().map(([key, label]) => [key, {
      key: `spec:${key}`, specKey: key, label, section: 'specs', type: 'spec', width: 16,
      aliases: [label, key.replace(/_/g, ' ')],
    }]),
  ).values(),
);

/** Të gjitha destinacionet e hartës: kolonat e shabllonit + specifikimet. */
export const ALL_COLUMNS = [...IMPORT_COLUMNS, ...SPEC_COLUMNS];
export const COLUMN_BY_KEY = ALL_COLUMNS.reduce((m, c) => { m[c.key] = c; return m; }, {});

/** Kokat e skedarit → `{ [columnKey]: headerIndex }` (−1 = e palidhur). */
export const detectMapping = (headers) => detectMappingFor(ALL_COLUMNS, headers);

/** ArrayBuffer (xlsx/xls/csv) → `{ headers, rows, sheetName, sheetNames }`. */
export const readWorkbook = (data, sheetName) => readWorkbookWith(XLSX, data, sheetName);

// ── Rreshtat shembull të shabllonit ──────────────────────────────────────────
//
// Shablloni vjen me tre rreshta të mbushur, sepse një fletë krejt bosh nuk e
// mëson askënd si shkruhet «Specifikimet» apo çfarë pranon «Statusi». Çmimi i
// tyre është i njohur: dikush do ta ngarkojë fletën pa i fshirë, dhe regjistri
// do të nisë me një laptop të shpikur. Prandaj ata rreshta NJIHEN këtu dhe
// ndalohen te ekrani — emri, seria dhe modeli bashkë, që një pajisje e vërtetë
// me emër të njëjtë të mos bjerë viktimë.
export const EXAMPLE_ROWS = [
  {
    name: 'Laptop Dell Latitude 5540', category: 'Laptop', subcategory: 'Ultrabook',
    brand: 'Dell', model: 'Latitude 5540', serialNumber: 'DL5540-KS-0142',
    internalCode: 'IT-2026-001', assetCode: '',
    condition: 'E re', status: 'E caktuar', location: 'Zyra 3, kati II',
    department: 'IT', responsible: '',
    purchaseDate: '2026-02-14', purchaseValue: '980', currency: 'EUR',
    supplier: 'Tech Store SH.P.K.', invoiceNo: 'F-2026-0142',
    paymentMethod: 'Transfer bankar', amortizationYears: '4', warrantyUntil: '2029-02-14',
    holder: 'Dorentina Qenaj', assignedOn: '2026-02-20',
    specsText: 'CPU: i7-1355U; RAM: 16 GB; Storage / SSD: 512 GB; Sistemi Operativ: Windows 11',
    accessories: 'Ngarkues; Çantë; Mouse', photo: 'laptop-dell-5540.jpg', description: '', notes: '',
  },
  {
    name: 'Automjet Volkswagen Caddy', category: 'Automjet', subcategory: 'Furgon',
    brand: 'Volkswagen', model: 'Caddy 2.0 TDI', serialNumber: 'WV1ZZZ2KZ9X012345',
    internalCode: 'AUTO-04', assetCode: '',
    condition: 'E mirë', status: 'Në dispozicion', location: 'Parkingu i kompanisë',
    department: 'Operacione', responsible: 'Art Safqi',
    purchaseDate: '2024-09-03', purchaseValue: '14500', currency: 'EUR',
    supplier: 'Auto Sallon Prishtina', invoiceNo: 'F-2024-0913',
    paymentMethod: 'Leasing', amortizationYears: '8', warrantyUntil: '2027-09-03',
    holder: '', assignedOn: '',
    specsText: 'Targat: 01-234-AB; Viti: 2024; Karburanti: Naftë; Kilometrazhi: 38400; Sigurimi deri më: 2026-09-02',
    accessories: 'Goma rezervë; Kriko; Set çelësash', photo: 'caddy-01-234-ab.jpg',
    description: 'Automjet i shërbimit teknik',
    notes: 'Servisi i radhës në 45.000 km',
  },
  {
    name: 'Karrige zyre ergonomike', category: 'Mobilje', subcategory: '',
    brand: 'Kinnarps', model: '6000', serialNumber: '',
    internalCode: 'MOB-118', assetCode: '',
    condition: 'E mirë', status: 'Në dispozicion', location: 'Salla e mbledhjeve',
    department: '', responsible: '',
    purchaseDate: '2025-05-19', purchaseValue: '180', currency: 'EUR',
    supplier: 'Mobileria Alba', invoiceNo: '', paymentMethod: 'Para në dorë',
    amortizationYears: '', warrantyUntil: '',
    holder: '', assignedOn: '',
    specsText: 'Materiali: Rrjetë + metal; Ngjyra: E zezë; Përmasat: 65×65×120 cm',
    accessories: '', photo: '', description: '', notes: '',
  },
];

const exampleKey = (name, serial, model) => [normKey(name), normKey(serial), normKey(model)].join('|');
const EXAMPLE_KEYS = new Set(EXAMPLE_ROWS.map((r) => exampleKey(r.name, r.serialNumber, r.model)));

// ── Validimi ─────────────────────────────────────────────────────────────────

const MSG = {
  required: (label) => `${label} është e detyrueshme`,
  badDate: (label) => `${label}: data nuk lexohet — u la bosh`,
  ambiguousDate: (label) => `${label}: ditë/muaj të paqarta — u lexua si ditë/muaj`,
  futureDate: (label) => `${label}: data është në të ardhmen`,
  dupInFile: (label) => `${label} përsëritet brenda skedarit`,
  dupInDb: (label) => `${label} ekziston tashmë te regjistri`,
  badNumber: (label) => `${label}: nuk është numër — u la bosh`,
  unknownValue: (label, v, fallback) => `${label}: «${v}» nuk njihet — u ruajt «${fallback}»`,
  droppedValue: (label, v) => `${label}: «${v}» nuk njihet — u la bosh`,
  example: 'Duket si rresht shembull i shabllonit — fshije para se ta ngarkosh',
  noWorker: (v) => `Mbajtësi «${v}» nuk u gjet te punonjësit — aseti hyn pa mbajtës`,
  noResponsible: (v) => `Personi përgjegjës «${v}» nuk u gjet — aseti hyn pa të`,
  noDepartment: (v) => `Departamenti «${v}» nuk u gjet — aseti hyn pa të`,
  assignedNoHolder: 'Statusi «E caktuar» pa mbajtës — u ruajt «Në dispozicion»',
  holderVsStatus: (s) => `Statusi «${s}» nuk pranon mbajtës. Hiq njërën nga të dyja.`,
  assignNoDate: 'Mbajtës pa datë dorëzimi — u vendos data e sotme',
  assignBeforeBuy: 'Dorëzimi është para datës së blerjes',
  warrantyBeforeBuy: 'Garancioni skadon para datës së blerjes — kontrollo të dyja datat',
  negativeValue: 'Vlera e blerjes është negative — u la bosh',
  badYears: 'Amortizimi duhet të jetë 1–50 vjet — u la bosh',
  strayspec: (list) => `Specifikimet pa «:» nuk u lexuan: ${list}`,
  noIdentifier: (label) => `${label} mungon — pajisja nuk dallohet dot nga një tjetër e njëjtë`,
  tooManyAccessories: (n) => `Vetëm 25 aksesorë hyjnë për aset; ${n} të tjerët u lanë jashtë`,
  noPhoto: (v) => `Fotoja «${v}» nuk u gjet mes skedarëve të zgjedhur — aseti hyn pa foto`,
};

const MAX_ACCESSORIES = 25;

/** Indeks emrash → objekt, i tolerueshëm ndaj shkronjave dhe thekseve. */
const lookupIndex = (list, fields) => {
  const idx = new Map();
  (list || []).forEach((item) => {
    fields.forEach((f) => {
      const v = normKey(typeof f === 'function' ? f(item) : item[f]);
      if (v && !idx.has(v)) idx.set(v, item);
    });
  });
  return idx;
};

const workerName = (w) => `${w?.FirstName || ''} ${w?.LastName || ''}`.trim() || w?.FullName || '';

/**
 * Rreshtat e papërpunuar → rreshta të gatshëm, me gabime dhe paralajmërime.
 *
 * Dallimi mban gjithë peshën: **gabimi** e ndalon rreshtin (pa emër, seri e
 * dyfishtë, mbajtës mbi një pajisje të humbur), **paralajmërimi** e lejon por e
 * thotë çfarë humbi (kategori e panjohur, punonjës që s'u gjet). Të dyja shihen
 * në ekran para se të shtypet «Importo» — dhe vetëm rreshtat pa gabime nisen.
 *
 * @param {Array[]} rows      rreshtat e skedarit, ashtu si u lexuan
 * @param {object}  mapping   `{ [columnKey]: headerIndex }`
 * @param {object}  ctx       `{ assets, workers, departments, today }`
 */
export function validateRows(rows, mapping, ctx = {}) {
  const {
    assets = [], workers = [], departments = [], today = todayISO(),
    photos = null,
  } = ctx;

  // `photos` është lista e emrave të skedarëve që zgjodhi njeriu. `null` do të
  // thotë «nuk zgjodhi asnjë» — dhe atëherë kolona e fotos rri e heshtur, sepse
  // ajo është opsionale. Vetëm kur foto JANË zgjedhur ka kuptim të thuhet se
  // njëra prej tyre nuk u gjet.
  const photoIdx = photos ? new Set(photos.map(photoKey).filter(Boolean)) : null;

  const deptIdx = lookupIndex(departments, ['Name', 'name', 'DepartmentName']);
  const workerIdx = lookupIndex(workers, [workerName, 'Email', 'PersonalNumber', 'EmployeeCode']);

  // Gjendja e regjistrit ashtu si ishte kur u hap dialogu. Serveri e kontrollon
  // sërish me të tanishmen — këtu vetëm që njeriu ta shohë përplasjen PARA se
  // ta nisë importin, jo si njëzet gabime të kthyera.
  const dbSerials = new Set(assets.map((a) => normKey(a.serialNumber)).filter(Boolean));
  const dbInternal = new Set(assets.map((a) => normKey(a.internalCode)).filter(Boolean));
  const dbCodes = new Set(assets.map((a) => normKey(a.assetCode)).filter(Boolean));

  const seenSerial = new Map();
  const seenInternal = new Map();
  const seenCode = new Map();

  const cell = (row, key) => {
    const i = mapping?.[key];
    return i === undefined || i < 0 ? '' : row[i];
  };
  const text = (row, key) => String(cell(row, key) ?? '').trim();

  return rows.map((row, i) => {
    const errors = [];
    const warnings = [];
    const values = {};
    const add = (list, key, message) => list.push({ key, message });
    const label = (key) => COLUMN_BY_KEY[key]?.label || key;

    // ── Identiteti ────────────────────────────────────────────────────────
    values.name = text(row, 'name');
    if (!values.name) add(errors, 'name', MSG.required(label('name')));

    values.brand = text(row, 'brand');
    values.model = text(row, 'model');
    values.subcategory = text(row, 'subcategory');
    values.description = text(row, 'description');
    values.notes = text(row, 'notes');
    values.location = text(row, 'location');
    values.supplier = text(row, 'supplier');
    values.invoiceNo = text(row, 'invoiceNo');

    if (EXAMPLE_KEYS.has(exampleKey(values.name, text(row, 'serialNumber'), values.model))) {
      add(errors, 'name', MSG.example);
    }

    // Kategoria bosh bie te «Tjetër» pa zhurmë — kolona mund të mos ekzistojë
    // fare te fleta e klientit. Vetëm një vlerë e SHKRUAR e panjohur ankohet:
    // aty njeriu e ka thënë diçka që sistemi s'e kuptoi.
    const rawCategory = text(row, 'category');
    values.category = parseCategory(rawCategory) || 'other';
    values.categoryText = rawCategory;
    if (rawCategory && !parseCategory(rawCategory)) {
      add(warnings, 'category', MSG.unknownValue(label('category'), rawCategory, categoryLabel('other')));
    }

    // ── Identifikuesit unikë ──────────────────────────────────────────────
    // Dy rreshta me të njëjtin numër serik do të thonë se pajisja u regjistrua
    // dy herë — dhe historiku i saj ndahet në dysh pa u vënë re. Baza e ndalon
    // me indeks; këtu ndalohet më herët, që njeriu ta shohë ku.
    for (const [key, seen, inDb] of [
      ['serialNumber', seenSerial, dbSerials],
      ['internalCode', seenInternal, dbInternal],
      ['assetCode', seenCode, dbCodes],
    ]) {
      const v = text(row, key);
      values[key] = v;
      if (!v) continue;
      const k = normKey(v);
      if (seen.has(k)) add(errors, key, `${MSG.dupInFile(label(key))} (rreshti ${seen.get(k) + 2})`);
      else if (inDb.has(k)) add(errors, key, MSG.dupInDb(label(key)));
      else seen.set(k, i);
    }

    if (!values.serialNumber && (values.category === 'phone' || values.category === 'vehicle')) {
      add(warnings, 'serialNumber', MSG.noIdentifier(identifierLabel(values.category)));
    }

    // ── Gjendja ───────────────────────────────────────────────────────────
    const rawCondition = text(row, 'condition');
    values.condition = parseCondition(rawCondition) || 'good';
    if (rawCondition && !parseCondition(rawCondition)) {
      add(warnings, 'condition', MSG.unknownValue(label('condition'), rawCondition, conditionLabel('good')));
    }

    const rawStatus = text(row, 'status');
    let status = parseStatus(rawStatus);
    if (rawStatus && !status) {
      add(warnings, 'status', MSG.unknownValue(label('status'), rawStatus, statusLabel('available')));
    }
    status = status || 'available';

    // ── Datat ─────────────────────────────────────────────────────────────
    for (const key of ['purchaseDate', 'warrantyUntil', 'assignedOn']) {
      const raw = cell(row, key);
      if (raw === '' || raw === null || raw === undefined) { values[key] = null; continue; }
      const { value, ambiguous } = parseDateCell(raw);
      values[key] = value;
      if (!value) { add(warnings, key, MSG.badDate(label(key))); continue; }
      if (ambiguous) add(warnings, key, MSG.ambiguousDate(label(key)));
      if (key !== 'warrantyUntil' && value > today) add(warnings, key, MSG.futureDate(label(key)));
    }
    // Garancioni para blerjes do të thotë se njëra prej dy datave është shtypur
    // gabim; serveri e refuzon rreshtin, ndaj ndalohet këtu ku ka kush e lexon.
    if (values.purchaseDate && values.warrantyUntil && values.warrantyUntil < values.purchaseDate) {
      add(errors, 'warrantyUntil', MSG.warrantyBeforeBuy);
    }

    // ── Financat ──────────────────────────────────────────────────────────
    const rawValue = text(row, 'purchaseValue');
    values.purchaseValue = rawValue ? parseNumber(cell(row, 'purchaseValue')) : null;
    if (rawValue && values.purchaseValue === null) {
      add(warnings, 'purchaseValue', MSG.badNumber(label('purchaseValue')));
    }
    if (values.purchaseValue !== null && values.purchaseValue < 0) {
      values.purchaseValue = null;
      add(warnings, 'purchaseValue', MSG.negativeValue);
    }

    const rawCurrency = text(row, 'currency');
    values.currency = parseCurrency(rawCurrency) || 'EUR';
    if (rawCurrency && !parseCurrency(rawCurrency)) {
      add(warnings, 'currency', MSG.unknownValue(label('currency'), rawCurrency, 'EUR'));
    }

    const rawPayment = text(row, 'paymentMethod');
    values.paymentMethod = parsePayment(rawPayment) || '';
    if (rawPayment && !values.paymentMethod) {
      add(warnings, 'paymentMethod', MSG.droppedValue(label('paymentMethod'), rawPayment));
    }

    const rawYears = text(row, 'amortizationYears');
    const years = rawYears ? parseNumber(cell(row, 'amortizationYears')) : null;
    values.amortizationYears = years !== null && years >= 1 && years <= 50 ? Math.round(years) : null;
    if (rawYears && values.amortizationYears === null) add(warnings, 'amortizationYears', MSG.badYears);

    // ── Departamenti dhe përgjegjësi ──────────────────────────────────────
    const rawDept = text(row, 'department');
    const dept = rawDept ? deptIdx.get(normKey(rawDept)) : null;
    values.departmentId = dept ? (dept.Id ?? dept.id ?? null) : null;
    values.departmentText = rawDept;
    if (rawDept && !dept) add(warnings, 'department', MSG.noDepartment(rawDept));

    const rawResp = text(row, 'responsible');
    const resp = rawResp ? workerIdx.get(normKey(rawResp)) : null;
    values.responsibleWorkerId = resp ? (resp.Id ?? resp.id ?? null) : null;
    values.responsibleText = rawResp;
    if (rawResp && !resp) add(warnings, 'responsible', MSG.noResponsible(rawResp));

    // ── Mbajtësi: pajisja që tashmë është në duar ─────────────────────────
    const rawHolder = text(row, 'holder');
    const holder = rawHolder ? workerIdx.get(normKey(rawHolder)) : null;
    values.holderWorkerId = holder ? (holder.Id ?? holder.id ?? null) : null;
    values.holderText = rawHolder;
    if (rawHolder && !holder) add(warnings, 'holder', MSG.noWorker(rawHolder));

    if (values.holderWorkerId) {
      // 'service', 'lost' dhe 'retired' e ndalojnë dorëzimin te serveri
      // (`canAssign`). Një rresht që i mban të dyja është kontradiktë e vetë
      // fletës, jo gabim leximi — ndaj e zgjidh njeriu, jo importi.
      if (status === 'service' || status === 'lost' || status === 'retired') {
        add(errors, 'holder', MSG.holderVsStatus(statusLabel(status)));
      }
      status = 'assigned';
      if (!values.assignedOn) {
        values.assignedOn = today;
        add(warnings, 'assignedOn', MSG.assignNoDate);
      }
      if (values.purchaseDate && values.assignedOn < values.purchaseDate) {
        add(warnings, 'assignedOn', MSG.assignBeforeBuy);
      }
    } else {
      // Statusi «E caktuar» pa mbajtës do të krijonte një pajisje që sistemi e
      // mendon te dikush, por nuk di te kush — dhe ajo nuk dorëzohet dot më.
      if (status === 'assigned') {
        status = 'available';
        add(warnings, 'status', MSG.assignedNoHolder);
      }
      values.assignedOn = null;
    }
    values.status = status;

    // ── Specifikimet: teksti i një kolone + kolonat e veçanta ─────────────
    const { specs, stray } = parseSpecsText(cell(row, 'specsText'));
    for (const col of SPEC_COLUMNS) {
      const v = text(row, col.key);
      if (v) specs[col.specKey] = v;
    }
    values.specs = specs;
    if (stray.length) add(warnings, 'specsText', MSG.strayspec(stray.join(' · ')));

    // Fotoja: mbahet emri ashtu si u shkrua (për ekranin) dhe çelësi i
    // përputhjes (për ngarkimin pas importit).
    const rawPhoto = text(row, 'photo');
    values.photoName = rawPhoto;
    values.photoKey = photoKey(rawPhoto);
    if (values.photoKey && photoIdx && !photoIdx.has(values.photoKey)) {
      add(warnings, 'photo', MSG.noPhoto(rawPhoto));
    }

    const acc = splitList(cell(row, 'accessories'));
    values.accessories = acc.slice(0, MAX_ACCESSORIES);
    if (acc.length > MAX_ACCESSORIES) {
      add(warnings, 'accessories', MSG.tooManyAccessories(acc.length - MAX_ACCESSORIES));
    }

    return { index: i, raw: row, values, errors, warnings, ok: errors.length === 0 };
  });
}

/**
 * Rreshti i validuar → trupi që pret `POST /api/assets/import`.
 *
 * `row` udhëton bashkë me të: pa numrin e rreshtit, një «dështoi» i kthyer nga
 * serveri është një gabim pa adresë te fleta e klientit.
 */
export function toPayload(r) {
  const v = r.values;
  const clean = (s) => (s ? String(s) : '');
  return {
    row: r.index + 2,
    name: v.name,
    category: v.category,
    subcategory: clean(v.subcategory),
    brand: clean(v.brand),
    model: clean(v.model),
    serialNumber: clean(v.serialNumber),
    internalCode: clean(v.internalCode),
    assetCode: clean(v.assetCode),
    condition: v.condition,
    status: v.status,
    location: clean(v.location),
    departmentId: v.departmentId || null,
    responsibleWorkerId: v.responsibleWorkerId || null,
    purchaseDate: v.purchaseDate || null,
    purchaseValue: v.purchaseValue === null ? null : v.purchaseValue,
    currency: v.currency || 'EUR',
    supplier: clean(v.supplier),
    invoiceNo: clean(v.invoiceNo),
    paymentMethod: v.paymentMethod || null,
    amortizationYears: v.amortizationYears,
    warrantyUntil: v.warrantyUntil || null,
    description: clean(v.description),
    notes: clean(v.notes),
    specs: v.specs || {},
    accessories: (v.accessories || []).map((name) => ({ name, included: true })),
    // Dorëzimi është veprim më vete te serveri, ndaj udhëton si objekt më vete:
    // aseti krijohet edhe kur caktimi dështon, dhe ajo thuhet te rezultati.
    assignment: v.holderWorkerId
      ? { workerId: v.holderWorkerId, workerName: v.holderText, assignedOn: v.assignedOn, condition: v.condition }
      : null,
  };
}

/** Përmbledhja që rri mbi tabelën e parashikimit. */
export function summarize(rows) {
  const ready = rows.filter((r) => r.ok);
  return {
    total: rows.length,
    ready: ready.filter((r) => r.warnings.length === 0).length,
    warned: ready.filter((r) => r.warnings.length > 0).length,
    blocked: rows.filter((r) => !r.ok).length,
    assigned: ready.filter((r) => r.values.holderWorkerId).length,
    uncategorized: ready.filter((r) => r.values.category === 'other' && !r.values.categoryText).length,
    accessories: ready.reduce((s, r) => s + (r.values.accessories?.length || 0), 0),
    photos: ready.filter((r) => r.values.photoKey && r.warnings.every((w) => w.key !== 'photo')).length,
    value: Math.round(ready.reduce((s, r) => s + (Number(r.values.purchaseValue) || 0), 0) * 100) / 100,
  };
}

// ── Shablloni ────────────────────────────────────────────────────────────────

const line = (rgb, side = 'bottom', style = 'thin') => ({ [side]: { style, color: { rgb } } });
const boxed = (rgb) => ({
  top: { style: 'thin', color: { rgb } }, bottom: { style: 'thin', color: { rgb } },
  left: { style: 'thin', color: { rgb } }, right: { style: 'thin', color: { rgb } },
});

const MONEY_FMT = '#,##0.00';
const DATE_FMT = 'yyyy-mm-dd';
// Seria, kodi i inventarit dhe kodi i asetit lexohen si TEKST me detyrim.
// Pa këtë, "0142" e humb zeron e parë dhe një IMEI pesëmbëdhjetë-shifror del si
// 3.5E+14 — dëm që ndodh brenda Excel-it, para se skedari ta shohë sistemi.
const TEXT_FMT = '@';

const FILL = { required: HEX.req, list: HEX.tint, free: HEX.soft };

/** Kolonat vlera e të cilave vjen nga një listë e mbyllur ose nga sistemi. */
const LIST_TYPES = new Set(['category', 'condition', 'status', 'payment', 'currency', 'department', 'worker']);

/** Çfarë pranon secili lloj kolone — një fjali, e njëjta te fleta e udhëzimeve. */
function acceptedText(c) {
  switch (c.type) {
    case 'category':   return `Njëra nga: ${CATEGORIES.map((x) => x.label).join(', ')}`;
    case 'condition':  return `Njëra nga: ${CONDITIONS.map((x) => x.label).join(', ')}`;
    case 'status':     return `Njëra nga: ${STATUSES.map((x) => x.label).join(', ')}`;
    case 'payment':    return `Njëra nga: ${PAYMENT_METHODS.map((x) => x.label).join(', ')}`;
    case 'currency':   return 'Kod tre-shkronjësh: EUR, USD, CHF… Bosh = EUR';
    case 'date':       return 'Datë: 2026-03-29, 29/03/2026 ose 29.03.2026';
    case 'money':      return 'Vetëm numri, pa simbolin e monedhës. 1250 ose 1250.50';
    case 'number':     return 'Numër i plotë';
    case 'department': return 'Emri i departamentit saktësisht si te sistemi (shih fletën «Listat»)';
    case 'worker':     return 'Emri i plotë i punonjësit si te sistemi (shih fletën «Listat»)';
    case 'specs':      return 'Çift «Emri: vlera», të ndara me pikëpresje — CPU: i7; RAM: 16 GB';
    case 'list':       return 'Të ndara me pikëpresje — Ngarkues; Çantë; Mouse';
    case 'photo':      return 'Emri i skedarit të fotos — laptop-dell-5540.jpg. Skedarët vetë zgjidhen te dialogu i importit.';
    default:           return 'Tekst i lirë';
  }
}

/** Emrat e punonjësve/departamenteve, ashtu si i pret importi. */
const namesOf = (list, pick) => (list || []).map(pick).map((s) => String(s || '').trim()).filter(Boolean);

/**
 * Shablloni `.xlsx` — fleta e të dhënave, udhëzimet dhe listat e vlerave.
 *
 * Tri fletë, sepse i shërbejnë tri momenteve të ndryshme: te **Asetet** shkruan,
 * te **Udhëzimet** kontrollon çfarë pranon një kolonë, te **Listat** kopjon emrin
 * e saktë të një departamenti ose punonjësi. Emrat NUK shkruhen me dorë te
 * shablloni: ata dalin nga sistemi, sepse mospërputhja e tyre («IT» kundrejt
 * «Teknologji Informative») është arsyeja e zakonshme pse gjysma e rreshtave
 * vjen pa departament.
 *
 * Excel-i nuk merr menu rënëse prej nesh: biblioteka që i shkruan këto skedarë
 * nuk di të shkruajë «data validation». Prandaj fleta «Listat» rri e ndarë dhe
 * e emërtuar — ajo është burimi që njeriu i lidh vetë me `Data ▸ Data
 * Validation ▸ List`, dhe ndërkohë shërben si kopje-ngjit.
 *
 * @param {object} o
 * @param {Array}  o.departments  departamentet ekzistuese
 * @param {Array}  o.workers      punonjësit ekzistues
 * @param {string} o.company      emri i firmës për kokën
 */
export function buildTemplateWorkbook({ departments = [], workers = [], company = '' } = {}) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheetAssets(company), 'Asetet');
  XLSX.utils.book_append_sheet(wb, sheetGuide(), 'Udhëzime');
  XLSX.utils.book_append_sheet(wb, sheetLists({ departments, workers }), 'Listat');
  return wb;
}

function sheetAssets(company) {
  const cols = IMPORT_COLUMNS;
  const last = cols.length - 1;
  const aoa = [];
  const merges = [];
  const ops = [];    // { r, c, s, z }
  const heights = [];
  const push = (row) => (aoa.push(row), aoa.length - 1);
  const style = (r, c, s, z) => ops.push({ r, c, s, z });
  const pad = (row) => { const out = row.slice(); while (out.length <= last) out.push(''); return out; };
  const band = (r, textStyle) => { merges.push({ s: { r, c: 0 }, e: { r, c: last } }); style(r, 0, textStyle); };

  const titleR = push(pad([company ? `${company} — REGJISTRI I ASETEVE` : 'REGJISTRI I ASETEVE']));
  band(titleR, {
    font: { bold: true, sz: 15, color: { rgb: HEX.white } },
    fill: { fgColor: { rgb: HEX.navy } },
    alignment: { vertical: 'center', indent: 1 },
  });
  heights[titleR] = { hpt: 30 };

  const subR = push(pad(['Shablloni i importit — plotëso një rresht për çdo pajisje. Mos i ndrysho emrat e kolonave.']));
  band(subR, {
    font: { sz: 10, color: { rgb: HEX.white } },
    fill: { fgColor: { rgb: HEX.brand } },
    alignment: { vertical: 'center', indent: 1 },
  });
  heights[subR] = { hpt: 20 };

  const legendR = push(pad([
    'E verdhë = e detyrueshme  ·  E kaltër = vlerë nga fleta «Listat»  ·  E bardhë = tekst i lirë  '
    + '·  Fshij tre rreshtat shembull para se ta ngarkosh  ·  Kolonat që s\'i ke, lëri bosh',
  ]));
  band(legendR, {
    font: { sz: 9, italic: true, color: { rgb: HEX.muted } },
    alignment: { vertical: 'center', indent: 1 },
  });
  heights[legendR] = { hpt: 18 };

  // Rreshti i seksioneve: një bandë e vetme mbi kokat, që tridhjetë kolona të
  // lexohen si pesë grupe. Ngjyra këtu shënon KUFIRIN e grupit, jo kuptimin —
  // kuptimin e mban ngjyra e kokës poshtë.
  const bandR = push(pad(cols.map((c, i) => (i === 0 || cols[i - 1].section !== c.section
    ? (SECTIONS.find((s) => s.key === c.section)?.label || '') : ''))));
  let from = 0;
  cols.forEach((c, i) => {
    style(bandR, i, {
      font: { bold: true, sz: 8, color: { rgb: HEX.white } },
      fill: { fgColor: { rgb: HEX.navy } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: line(HEX.white, 'right'),
    });
    const isLast = i === last || cols[i + 1].section !== c.section;
    if (isLast) { if (i > from) merges.push({ s: { r: bandR, c: from }, e: { r: bandR, c: i } }); from = i + 1; }
  });
  heights[bandR] = { hpt: 16 };

  const headR = push(cols.map((c) => (c.required ? `${c.label} *` : c.label)));
  cols.forEach((c, i) => style(headR, i, {
    font: { bold: true, sz: 9, color: { rgb: HEX.ink } },
    fill: { fgColor: { rgb: c.required ? FILL.required : LIST_TYPES.has(c.type) ? FILL.list : FILL.free } },
    border: boxed(HEX.rule),
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  }));
  heights[headR] = { hpt: 30 };

  // Tre rreshta shembull, me qëllim të ndryshëm nga njëri-tjetri: një laptop që
  // dikush e mban (mbajtësi + data e dorëzimit), një automjet në depo me
  // specifikime krejt të tjera, dhe një karrige ku shumica e kolonave rrinë
  // bosh — që të mos duket se çdo kolonë duhet mbushur.
  const firstExampleR = headR + 1;
  EXAMPLE_ROWS.forEach((ex, n) => {
    const r = push(cols.map((c) => ex[c.key] ?? ''));
    cols.forEach((c, i) => style(r, i, {
      font: { sz: 9, italic: true, color: { rgb: HEX.muted } },
      fill: { fgColor: { rgb: n % 2 ? HEX.soft : HEX.white } },
      border: line(HEX.rule),
      alignment: { vertical: 'center', wrapText: false, indent: 1 },
    }, numberFormat(c)));
  });
  const lastExampleR = headR + EXAMPLE_ROWS.length;

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map((c) => ({ wch: c.width || 16 }));
  ws['!merges'] = merges;
  ws['!rows'] = heights;
  // Ngrirja mban kokën dhe kolonën e emrit në ekran: te kolona e njëzetetretë
  // ("Data e dorëzimit") askush s'e mban mend cili rresht ishte.
  ws['!freeze'] = { xSplit: 1, ySplit: firstExampleR };
  ws['!autofilter'] = {
    ref: `${XLSX.utils.encode_cell({ r: headR, c: 0 })}:${XLSX.utils.encode_cell({ r: lastExampleR, c: last })}`,
  };
  ops.forEach(({ r, c, s, z }) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (!ws[addr]) return;
    if (s) ws[addr].s = s;
    if (z) ws[addr].z = z;
  });
  return ws;
}

function numberFormat(c) {
  if (c.type === 'money') return MONEY_FMT;
  if (c.type === 'date') return DATE_FMT;
  if (c.key === 'serialNumber' || c.key === 'internalCode' || c.key === 'assetCode') return TEXT_FMT;
  return undefined;
}

function sheetGuide() {
  const rows = [
    ['SI PLOTËSOHET SHABLLONI', ''],
    ['Një rresht = një pajisje. Vetëm «Emri i asetit» është i detyrueshëm; çdo kolonë tjetër mund të mbetet bosh.', ''],
    ['', ''],
  ];
  const sectionRows = [];
  const headRows = [];

  SECTIONS.filter((s) => s.key !== 'specs').forEach((sec) => {
    const items = IMPORT_COLUMNS.filter((c) => c.section === sec.key);
    if (!items.length) return;
    sectionRows.push(rows.length);
    rows.push([sec.label.toUpperCase(), '']);
    headRows.push(rows.length);
    rows.push(['Kolona', 'Çfarë pranohet']);
    items.forEach((c) => rows.push([
      c.required ? `${c.label} *` : c.label, acceptedText(c),
    ]));
    rows.push(['', '']);
  });

  const bullets = [
    ['ÇFARË DUHET DITUR', ''],
    ['Kodi i asetit', 'Bosh → sistemi e jep vetë (AST-00042). Kodi yt i vjetër, nëse e shkruan, ruhet ashtu siç është.'],
    ['Numri serik / IMEI / VIN', 'Një pajisje FIZIKE, një numër. Dy rreshta me të njëjtin numër nuk hyjnë të dy — sistemi e ndalon, sepse historiku i tyre do të ndahej në dysh.'],
    ['Mbajtësi aktual', 'Kjo është kolona më e rëndësishme e fletës. E mbushur, pajisja hyn e DORËZUAR te ai punonjës: hapet caktimi, statusi bëhet «E caktuar» dhe historiku nis me atë datë. Bosh, pajisja hyn në depo.'],
    ['Data e dorëzimit', 'Bosh me mbajtës të shkruar → merret data e sotme, dhe kjo thuhet te parashikimi.'],
    ['Personi përgjegjës', 'Nuk është mbajtësi. Përgjegjësi i përgjigjet për pajisjen (p.sh. shefi i IT-së); mbajtësi e ka në duar.'],
    ['Statusi', '«E caktuar» pa mbajtës nuk ruhet — pajisja do të mbetej e bllokuar te askushi. «Në servis», «E humbur» dhe «E larguar» nuk shkojnë bashkë me një mbajtës.'],
    ['Gjendja fizike', 'E ndarë me qëllim nga statusi: një pajisje E DËMTUAR mund të jetë ende te dikush, dhe një E RE mund të rrijë në depo.'],
    ['Specifikimet', 'Një kolonë, çifte «Emri: vlera» të ndara me pikëpresje. Emrat e njohur (CPU, RAM, Targat, Kilometrazhi) grupohen vetvetiu; një emër i shpikur ruhet ashtu siç e shkruan.'],
    ['Specifikime si kolona', 'Nëse fleta jote i ka CPU-në, RAM-in ose Targat si kolona më vete, mos i bashko — sistemi i njeh edhe ashtu te hapi i hartës së kolonave.'],
    ['Aksesorët', 'Të ndara me pikëpresje. Hyjnë si rreshta më vete, jo si tekst — që te kthimi të mund të shënohet cili u kthye e cili jo.'],
    ['Fotoja', 'Fotot nuk hyjnë dot brenda Excel-it. Shkruaj EMRIN e skedarit (laptop-dell-5540.jpg) dhe, te dialogu i importit, zgjidh vetë fotot — sistemi i përputh me rreshtat dhe i ngarkon pas krijimit. Kolona është krejt opsionale; pa të, aseti hyn pa foto dhe fotoja shtohet më vonë nga kartela e tij.'],
    ['Vlera e blerjes', 'Vetëm numri. Monedha rri te kolona e vet; bosh lexohet si EUR.'],
    ['Amortizimi', 'Vitet e jetës së dobishme (1–50). Pa të, «vlera e mbetur» nuk llogaritet dot — dhe pikërisht ajo shifër i duhet kontabilitetit.'],
    ['Garancioni', 'Nuk mund të skadojë para datës së blerjes; ai rresht ndalet, sepse njëra nga dy datat është shtypur gabim.'],
    ['Datat', '«2026-03-29», «29/03/2026» dhe «29.03.2026» lexohen të tria. Kur dita dhe muaji janë të dyja nën 13, lexohet DITË/MUAJ dhe rreshti shënohet — kontrolloje.'],
    ['Rreshtat shembull', 'Fshiji para ngarkimit. Nëse harrohen, sistemi i njeh dhe i ndal — por atëherë humb një hap kohë.'],
    ['', ''],
    ['MENU RËNËSE NË EXCEL (opsionale)', ''],
    ['Si bëhet', 'Zgjidh kolonën te fleta «Asetet» ▸ Data ▸ Data Validation ▸ Allow: List ▸ Source: zona përkatëse te fleta «Listat». Kështu vlerat zgjidhen, nuk shkruhen — dhe asnjë «In use» i shkruar ndryshe nuk mbetet i palexuar.'],
    ['', ''],
    ['PAS NGARKIMIT', ''],
    ['Harta e kolonave', 'Sistemi i njeh vetë kolonat e fletës sate, edhe kur janë anglisht. Ti vetëm i konfirmon — dhe këtë hap nuk e kalon askush në vendin tënd.'],
    ['Parashikimi', 'Çdo rresht shihet para se të dërgohet: çka hyn, çka hyn me vërejtje dhe çka nuk hyn fare.'],
    ['Rreshtat me problem', 'Shkarkohen si skedar më vete, me arsyen përbri secilit — rregullohen aty dhe ngarkohen sërish vetëm ata.'],
  ];
  bullets.forEach((b) => {
    if (b[0] && !b[1]) sectionRows.push(rows.length);
    rows.push(b);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 34 }, { wch: 104 }];
  ws['!rows'] = rows.map((r) => (String(r[1] || '').length > 100 ? { hpt: 30 } : undefined));
  ws['!merges'] = [];

  const st = (r, c, s) => { const cell = ws[XLSX.utils.encode_cell({ r, c })]; if (cell) cell.s = s; };
  rows.forEach((r, i) => {
    st(i, 0, { font: { sz: 10, bold: true, color: { rgb: HEX.ink } }, alignment: { vertical: 'top', wrapText: true, indent: 1 } });
    st(i, 1, { font: { sz: 10, color: { rgb: HEX.muted } }, alignment: { vertical: 'top', wrapText: true, indent: 1 } });
  });
  st(0, 0, { font: { bold: true, sz: 14, color: { rgb: HEX.white } }, fill: { fgColor: { rgb: HEX.navy } }, alignment: { vertical: 'center', indent: 1 } });
  ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } });
  ws['!rows'][0] = { hpt: 26 };
  st(1, 0, { font: { sz: 10, italic: true, color: { rgb: HEX.muted } }, alignment: { vertical: 'center', indent: 1 } });
  ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: 1 } });

  sectionRows.filter((r) => r > 1).forEach((r) => {
    st(r, 0, {
      font: { bold: true, sz: 9, color: { rgb: HEX.brand } },
      fill: { fgColor: { rgb: HEX.tint } },
      border: line(HEX.brand, 'bottom', 'medium'),
      alignment: { vertical: 'center', indent: 1 },
    });
    st(r, 1, { fill: { fgColor: { rgb: HEX.tint } }, border: line(HEX.brand, 'bottom', 'medium') });
  });
  headRows.forEach((r) => {
    [0, 1].forEach((c) => st(r, c, {
      font: { bold: true, sz: 8, color: { rgb: HEX.muted } },
      border: line(HEX.rule),
      alignment: { vertical: 'center', indent: 1 },
    }));
  });
  return ws;
}

/**
 * Fleta «Listat» — çdo vlerë e pranuar, në kolonën e vet.
 *
 * Ekziston për dy përdorime: kopje-ngjit kur dikush plotëson me dorë, dhe
 * burim për menutë rënëse që njeriu i lidh vetë te Excel-i. Departamentet dhe
 * punonjësit vijnë NGA SISTEMI, jo nga një listë e shkruar këtu — përndryshe
 * shablloni do të plakej në ditën e parë.
 */
function sheetLists({ departments, workers }) {
  const columns = [
    { head: 'Kategoritë', values: CATEGORIES.map((x) => x.label) },
    { head: 'Gjendja fizike', values: CONDITIONS.map((x) => x.label) },
    { head: 'Statuset', values: STATUSES.map((x) => x.label) },
    { head: 'Mënyra e pagesës', values: PAYMENT_METHODS.map((x) => x.label) },
    { head: 'Monedhat', values: ['EUR', 'USD', 'CHF', 'GBP'] },
    { head: 'Departamentet', values: namesOf(departments, (d) => d.Name || d.name || d.DepartmentName) },
    { head: 'Punonjësit', values: namesOf(workers, (w) => workerName(w)).sort((a, b) => a.localeCompare(b, 'sq')) },
  ];
  const depth = Math.max(...columns.map((c) => c.values.length), 1);

  const aoa = [
    ['LISTAT E VLERAVE TË PRANUARA'],
    ['Kopjoji prej këtu, ose lidhi si «Data ▸ Data Validation ▸ List» te fleta «Asetet».'],
    columns.map((c) => c.head),
  ];
  for (let i = 0; i < depth; i += 1) {
    aoa.push(columns.map((c) => c.values[i] ?? ''));
  }
  // Kolona bosh nuk lihet e heshtur: «(asnjë ende)» e thotë se sistemi nuk ka
  // departamente, jo se shablloni harroi t'i shkruajë.
  columns.forEach((c, i) => { if (!c.values.length) aoa[3][i] = '(asnjë ende)'; });

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = columns.map((c) => ({ wch: Math.max(16, Math.min(30, c.head.length + 8)) }));
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: columns.length - 1 } },
  ];
  ws['!rows'] = [{ hpt: 26 }, { hpt: 18 }, { hpt: 20 }];
  ws['!freeze'] = { xSplit: 0, ySplit: 3 };

  const st = (r, c, s) => { const cell = ws[XLSX.utils.encode_cell({ r, c })]; if (cell) cell.s = s; };
  st(0, 0, { font: { bold: true, sz: 14, color: { rgb: HEX.white } }, fill: { fgColor: { rgb: HEX.navy } }, alignment: { vertical: 'center', indent: 1 } });
  st(1, 0, { font: { sz: 9, italic: true, color: { rgb: HEX.muted } }, alignment: { vertical: 'center', indent: 1 } });
  columns.forEach((_, c) => st(2, c, {
    font: { bold: true, sz: 9, color: { rgb: HEX.ink } },
    fill: { fgColor: { rgb: HEX.tint } },
    border: boxed(HEX.rule),
    alignment: { horizontal: 'center', vertical: 'center' },
  }));
  for (let r = 3; r < aoa.length; r += 1) {
    columns.forEach((_, c) => st(r, c, {
      font: { sz: 9, color: { rgb: HEX.ink } },
      border: line(HEX.rule),
      alignment: { vertical: 'center', indent: 1 },
    }));
  }
  return ws;
}

export function downloadTemplate(ctx) {
  XLSX.writeFile(buildTemplateWorkbook(ctx), 'nexuma-import-asetet.xlsx');
}

/**
 * Raporti i asaj që NUK hyri — rreshti origjinal plus arsyeja, që klienti ta
 * rregullojë në vend dhe ta ngarkojë sërish vetëm atë. Pa këtë, «7 nga 210
 * dështuan» është një numër pa asnjë rrugë përpara.
 */
export function buildErrorWorkbook(rows, headers) {
  const bad = rows.filter((r) => !r.ok || r.warnings.length);
  const aoa = [
    ['Rreshti', 'Statusi', 'Arsyeja', ...(headers || [])],
    ...bad.map((r) => [
      r.index + 2,
      r.ok ? 'U importua me vërejtje' : 'NUK u importua',
      [...r.errors, ...r.warnings].map((e) => e.message).join(' · '),
      ...(r.raw || []).map((c) => (c instanceof Date ? parseDateCell(c).value : c)),
    ]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 8 }, { wch: 22 }, { wch: 70 }, ...(headers || []).map(() => ({ wch: 18 }))];
  ws['!freeze'] = { xSplit: 3, ySplit: 1 };
  (headers || []).concat(['', '', '']).forEach((_, c) => {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
    if (cell) {
      cell.s = {
        font: { bold: true, sz: 9, color: { rgb: HEX.ink } },
        fill: { fgColor: { rgb: c < 3 ? HEX.req : HEX.soft } },
        border: boxed(HEX.rule),
        alignment: { vertical: 'center', wrapText: true },
      };
    }
  });
  bad.forEach((r, i) => {
    const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c: 2 })];
    if (cell) cell.s = { font: { sz: 9, color: { rgb: r.ok ? HEX.muted : 'B42318' } }, alignment: { vertical: 'top', wrapText: true } };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Rreshtat me problem');
  return wb;
}

export function downloadErrorReport(rows, headers) {
  XLSX.writeFile(buildErrorWorkbook(rows, headers), 'nexuma-import-asetet-gabimet.xlsx');
}
